import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Modal } from '@/components';
import { Alert } from '@/design-system/alert';
import { Switch } from '@/design-system/switch';

import { warehouseApi } from '../api/warehouse.api';
import { fmtQty, norm } from '../format';
import { type FieldTarget, useServerErrors } from '../hooks/useServerErrors';
import { useWizard } from '../hooks/useWizard';
import {
  cbmOf,
  draftFromSku,
  emptySkuDraft,
  packagingHints,
  readSkuDraft,
  SKU_GROUPS,
  SKU_PROP,
  SKU_STATUS_LABEL,
  SKU_STEP_OF,
  type SkuBoolKey,
  type SkuDraft,
  type SkuPropDef,
  type SkuPropKey,
} from '../skuMaster';
import {
  WAREHOUSE_UOMS,
  type ClientRef,
  type CreateSkuPayload,
  type FieldError,
  type SkuRowPayload,
  type WarehouseSku,
} from '../types';
import { EnumSelect, FormField, TextInput } from './form';
import { ImageUpload } from './ImageUpload';
import { ClientSelect } from './pickers';
import { KVGrid, metaCls, StatusBadge } from './ui';
import { WizardFooter, WizardSteps } from './wizard';

const UOM_OPTIONS = WAREHOUSE_UOMS.map((u) => ({ value: u, label: u }));
const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: SKU_STATUS_LABEL.ACTIVE },
  { value: 'INACTIVE', label: SKU_STATUS_LABEL.INACTIVE },
];

/* --------------------------- server 422 → field -------------------------- */

type FormKey = SkuPropKey | 'client' | 'form';

const PROP_BY_NORM = new Map<string, SkuPropKey>(
  Object.keys(SKU_PROP).map((k) => [norm(k), k as SkuPropKey])
);

/** Backend field (`itemName`, `weightKg`, `clientID`, …) → SKU form field. */
export const skuFieldOf = (e: FieldError): FieldTarget<FormKey> => {
  const n = norm(e.field || '');
  if (!n) return { key: 'form' };
  if (n.startsWith('client')) return { key: 'client' };
  if (n === 'uom') return { key: 'uomName' };
  return { key: PROP_BY_NORM.get(n) ?? 'form' };
};

const REVIEW_STEP = SKU_GROUPS.length + 1;
const skuStepOf = (k: FormKey): number =>
  k === 'client' ? 1 : k === 'form' ? REVIEW_STEP : SKU_STEP_OF[k];

/** Code-only conflicts pinned to the field that fixes them. */
const SKU_CODE_FIELD: Readonly<Record<string, string>> = {
  DUPLICATE_SKU: 'sku',
  SKU_EXISTS: 'sku',
};

/* ------------------------------- inputs ---------------------------------- */

const PropInput: React.FC<{
  def: SkuPropDef;
  draft: SkuDraft;
  onChange: (patch: Partial<SkuDraft>) => void;
  error: string | null;
  idPrefix: string;
  readOnly?: boolean;
}> = ({ def, draft, onChange, error, idPrefix, readOnly }) => {
  const id = `${idPrefix}-${def.key}`;
  if (def.kind === 'bool') {
    const key = def.key as SkuBoolKey;
    return (
      <div className="flex items-start justify-between gap-3 rounded-lg border border-stroke-soft-200 px-3 py-2.5">
        <div className="min-w-0">
          <label
            htmlFor={id}
            className="text-[13px] font-medium text-text-strong-950"
          >
            {def.label}
          </label>
          {def.hint && <p className={metaCls}>{def.hint}</p>}
        </div>
        <Switch
          id={id}
          checked={draft[key]}
          onCheckedChange={(v) => onChange({ [key]: v === true })}
        />
      </div>
    );
  }
  const value = String(draft[def.key as Exclude<SkuPropKey, SkuBoolKey>]);
  const set = (v: string) => onChange({ [def.key]: v } as Partial<SkuDraft>);
  if (def.kind === 'image') {
    return (
      <div className="sm:col-span-2">
        <FormField
          label={def.label}
          optional
          hint={def.hint}
          error={error}
          testId={id}
        >
          <ImageUpload
            value={value || null}
            onChange={(url) => set(url ?? '')}
            idPrefix={id}
            testId={`${id}-upload`}
          />
        </FormField>
      </div>
    );
  }
  return (
    <FormField
      label={def.label}
      htmlFor={id}
      required={def.required}
      optional={!def.required && def.kind !== 'status'}
      error={error}
    >
      {def.kind === 'uom' ? (
        <EnumSelect
          id={id}
          value={value}
          onChange={set}
          options={UOM_OPTIONS}
          invalid={!!error}
        />
      ) : def.kind === 'status' ? (
        <EnumSelect
          id={id}
          value={value}
          onChange={set}
          options={STATUS_OPTIONS}
          invalid={!!error}
        />
      ) : (
        <TextInput
          id={id}
          size="md"
          value={value}
          onChange={set}
          disabled={readOnly}
          invalid={!!error}
          inputMode={
            def.kind === 'decimal'
              ? 'decimal'
              : def.kind === 'int'
                ? 'numeric'
                : undefined
          }
          className={def.kind === 'code' ? 'font-mono' : undefined}
        />
      )}
    </FormField>
  );
};

/* -------------------------- read-only sections --------------------------- */

const yesNo = (v: unknown) => (v === true ? 'Ya' : 'Tidak');

const shown = (def: SkuPropDef, v: unknown): React.ReactNode => {
  if (def.kind === 'bool') return yesNo(v);
  if (v === null || v === undefined || v === '') return null;
  if (def.kind === 'status')
    return (
      <StatusBadge
        meta={
          v === 'INACTIVE'
            ? { label: SKU_STATUS_LABEL.INACTIVE, tone: 'faded' }
            : { label: SKU_STATUS_LABEL.ACTIVE, tone: 'success' }
        }
      />
    );
  if (def.kind === 'decimal' || def.kind === 'int') return fmtQty(Number(v));
  if (def.kind === 'image')
    return (
      <a
        href={String(v)}
        target="_blank"
        rel="noreferrer"
        className="inline-block"
        data-testid="sku-image-link"
      >
        <img
          src={String(v)}
          alt="Foto barang"
          className="size-14 rounded-md border border-stroke-soft-200 object-cover"
        />
      </a>
    );
  return String(v);
};

/**
 * The SKU master in its groups (Identitas, Klasifikasi, Ukuran & Kemasan,
 * Handling & Tracking) — the form's Review step and the detail panel.
 */
export const SkuSections: React.FC<{
  sku: Partial<SkuRowPayload>;
  client?: string | null;
}> = ({ sku, client }) => {
  const rec = sku as Record<string, unknown>;
  const cbm = cbmOf(sku);
  const hints = packagingHints(sku);
  return (
    <div className="flex flex-col gap-5" data-testid="sku-sections">
      {SKU_GROUPS.map((g) => (
        <section key={g.id} aria-label={g.label}>
          <p className="label-eyebrow">{g.label}</p>
          <KVGrid
            items={[
              ...(g.id === 1 && client !== undefined
                ? ([['Client', client]] as [string, React.ReactNode][])
                : []),
              ...g.keys.map(
                (k) =>
                  [SKU_PROP[k].label, shown(SKU_PROP[k], rec[k])] as [
                    string,
                    React.ReactNode,
                  ]
              ),
              ...(g.id === 3
                ? ([['CBM / unit', cbm === null ? null : cbm.toFixed(4)]] as [
                    string,
                    React.ReactNode,
                  ][])
                : []),
            ]}
          />
          {g.id === 3 && hints.length > 0 && (
            <p
              className={`${metaCls} mt-2 text-(--state-warning-dark)`}
              data-testid="sku-packaging-hint"
            >
              <i className="ri-information-line mr-1" aria-hidden />
              {hints.join(' ')}
            </p>
          )}
        </section>
      ))}
    </div>
  );
};

/* ------------------------------ form (stepper) --------------------------- */

const STEPS = [
  ...SKU_GROUPS.map((g) => ({ id: g.id, label: g.label })),
  { id: REVIEW_STEP, label: 'Review' },
];

/**
 * Create / edit a SKU as a stepper whose steps are the property groups, then
 * Review. Labels are the sheet's property names (ops know them by those);
 * group headings are Indonesian. Edit uses PATCH — SKU Code and Client are
 * fixed. A 422 lists every problem and jumps to the earliest step. A host form
 * (PO lines) can lock the Client to its own and prefill the SKU Code.
 */
export const SkuFormModal: React.FC<{
  open: boolean;
  /** null = create. */
  sku?: WarehouseSku | null;
  initialClient: ClientRef | null;
  /** Create only: the Client is fixed to `initialClient` (read-only). */
  lockedClient?: boolean;
  /** Create only: prefill for SKU Code (what the user was looking for). */
  initialSku?: string;
  /** Opened over another modal (e.g. from the PO form). */
  stacked?: boolean;
  onClose: () => void;
  onSaved: (sku: WarehouseSku, mode: 'create' | 'edit') => void;
}> = ({
  open,
  sku = null,
  initialClient,
  lockedClient = false,
  initialSku = '',
  stacked = false,
  onClose,
  onSaved,
}) => {
  const editing = !!sku;
  const [client, setClient] = useState<ClientRef | null>(initialClient);
  const [draft, setDraft] = useState<SkuDraft>(emptySkuDraft());
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const server = useServerErrors(skuFieldOf, skuStepOf);
  const { errors, setErrors, fieldError: fe } = server;

  const localErrorsOf = (s: number): FieldError[] => {
    const out = readSkuDraft(draft).errors.filter(
      (e) => SKU_STEP_OF[e.field as SkuPropKey] === s
    );
    if (s === 1 && !editing && !client)
      out.unshift({ field: 'clientID', message: 'Client wajib dipilih.' });
    return out;
  };
  const wizard = useWizard<FieldError>({
    total: STEPS.length,
    check: localErrorsOf,
  });
  const { step } = wizard;
  const resetWizard = wizard.reset;

  useEffect(() => {
    if (!open) return;
    resetWizard();
    setClient(sku?.client ?? initialClient);
    setDraft(sku ? draftFromSku(sku) : emptySkuDraft(initialSku));
    setErrors([]);
    setFormError(null);
    setBusy(false);
  }, [open, sku, initialClient, initialSku, resetWizard, setErrors]);

  const onChange = useCallback(
    (patch: Partial<SkuDraft>) => {
      setDraft((d) => ({ ...d, ...patch }));
      // Typing in a field clears its error.
      const keys = Object.keys(patch);
      setErrors((prev) =>
        prev.filter((e) => !keys.includes(skuFieldOf(e).key))
      );
    },
    [setErrors]
  );

  const reviewValue = useMemo(() => readSkuDraft(draft).value, [draft]);

  if (!open) return null;

  const next = () => {
    const local = wizard.next();
    setErrors((prev) => [
      ...prev.filter((e) => server.stepOfError(e) !== step),
      ...local,
    ]);
    setFormError(null);
  };
  const back = () => {
    setFormError(null);
    wizard.back();
  };

  const submit = async () => {
    const local = wizard.checkAll();
    const { value } = readSkuDraft(draft);
    if (local.length || !value || (!editing && !client)) {
      setErrors(local);
      return;
    }
    setBusy(true);
    setErrors([]);
    setFormError(null);
    try {
      if (sku) {
        const { sku: _code, ...changes } = value;
        onSaved(await warehouseApi.updateSku(sku.id, changes), 'edit');
      } else if (client) {
        const body: CreateSkuPayload = {
          ...value,
          clientID: client.id,
          clientName: client.name,
        };
        onSaved(await warehouseApi.createSku(body), 'create');
      }
    } catch (err) {
      const res = server.fromError(err, SKU_CODE_FIELD);
      if (res.step !== null) wizard.goTo(res.step);
      else setFormError(res.message);
    } finally {
      setBusy(false);
    }
  };

  const group = SKU_GROUPS.find((g) => g.id === step);
  const hints = step === 3 ? packagingHints(draft) : [];
  const cbm = step === 3 ? cbmOf(readSkuDraft(draft).value ?? {}) : null;
  const stepLabel = (e: FieldError) => STEPS[server.stepOfError(e) - 1]?.label;

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      icon="ri-barcode-line"
      title={editing ? `Edit SKU ${sku?.sku}` : 'Tambah SKU'}
      subtitle={
        editing ? (sku?.client?.name ?? `Client #${sku?.clientID}`) : undefined
      }
      width="xl"
      stacked={stacked}
      confirmOnClose={!busy}
      closeOnBackdropClick={!busy}
      closeOnEscape={!busy}
      footer={
        <WizardFooter
          step={step}
          total={STEPS.length}
          onCancel={onClose}
          onBack={back}
          onNext={next}
          onSubmit={() => void submit()}
          submitLabel={editing ? 'Simpan SKU' : 'Tambah SKU'}
          submitTestId="sku-submit"
          busy={busy}
        />
      }
    >
      <div className="flex flex-col gap-4" data-testid="sku-form">
        <WizardSteps steps={STEPS} current={step} />
        {errors.length > 0 && (
          <Alert
            status="error"
            appearance="stroke"
            size="lg"
            title={`${errors.length} isian perlu diperbaiki`}
            data-testid="sku-errors"
          >
            <ul className="list-disc pl-4">
              {errors.map((e, i) => (
                <li key={`${i}-${e.message}`}>
                  {server.stepOfError(e) !== step && (
                    <span className="text-text-sub-600">{stepLabel(e)} · </span>
                  )}
                  {e.message}
                </li>
              ))}
            </ul>
          </Alert>
        )}
        {formError && (
          <Alert
            status="error"
            appearance="stroke"
            size="lg"
            title="Gagal menyimpan"
          >
            {formError}
          </Alert>
        )}

        {step === 1 && (
          <FormField label="Client" required error={fe('client')}>
            {editing || lockedClient ? (
              <p className="text-sm font-medium text-text-strong-950">
                {client?.name ?? `Client #${sku?.clientID}`}
              </p>
            ) : (
              <ClientSelect
                value={client}
                onChange={setClient}
                invalid={!!fe('client')}
              />
            )}
          </FormField>
        )}

        {group && (
          <div
            className={
              group.id === 4
                ? 'flex flex-col gap-3'
                : 'grid grid-cols-1 gap-3 sm:grid-cols-2'
            }
            data-testid={`sku-step-${group.id}`}
          >
            {group.id === 4 ? (
              <>
                <PropInput
                  def={SKU_PROP.storageType}
                  draft={draft}
                  onChange={onChange}
                  error={fe('storageType')}
                  idPrefix="sku"
                />
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {group.keys
                    .filter((k) => SKU_PROP[k].kind === 'bool')
                    .map((k) => (
                      <PropInput
                        key={k}
                        def={SKU_PROP[k]}
                        draft={draft}
                        onChange={onChange}
                        error={fe(k)}
                        idPrefix="sku"
                      />
                    ))}
                </div>
              </>
            ) : (
              group.keys.map((k) => (
                <PropInput
                  key={k}
                  def={SKU_PROP[k]}
                  draft={draft}
                  onChange={onChange}
                  error={fe(k)}
                  idPrefix="sku"
                  readOnly={editing && k === 'sku'}
                />
              ))
            )}
          </div>
        )}

        {step === 3 && (
          <div className="flex flex-col gap-2">
            <p className={metaCls} data-testid="sku-cbm">
              CBM / unit:{' '}
              <span className="font-medium text-text-strong-950">
                {cbm === null ? '—' : `${cbm.toFixed(4)} m³`}
              </span>
            </p>
            {hints.length > 0 && (
              <Alert
                status="warning"
                appearance="stroke"
                size="lg"
                title="Cek angka kemasan"
                data-testid="sku-packaging-hint"
              >
                {hints.join(' ')} Tetap bisa disimpan.
              </Alert>
            )}
          </div>
        )}

        {step === REVIEW_STEP && reviewValue && (
          <div data-testid="sku-review" className="flex flex-col gap-3">
            {editing &&
              sku &&
              (sku.status ?? 'ACTIVE') !== reviewValue.status && (
                <p
                  className="text-sm text-text-sub-600"
                  data-testid="sku-status-change"
                >
                  {reviewValue.status === 'INACTIVE'
                    ? 'SKU dinonaktifkan: tidak bisa dipakai di PO dan outbound request baru.'
                    : 'SKU diaktifkan kembali.'}
                </p>
              )}
            <SkuSections
              sku={reviewValue}
              client={client?.name ?? `Client #${sku?.clientID}`}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};
